/* eslint-disable @next/next/link-passhref */
import React, { useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import cookies from '@utils/cookies';
import { segment } from '@actions/analytics';
import {
  CHECKOUT_STARTED,
  SYLLABUS,
  SYLLABUS_CARD_CLICKED,
  SYLLABUS_CLICKED_KEYS
} from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { evtNameCategoryMap } from '@constants/index';
import pushRoute from '@utils/pushRoute';
import useSyllabus from '@api/hooks/goal/topics/useSyllabus';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getActiveUidsByType } from '@models/Me';
import Header, { ISyllabusHomePageHeaderProps } from './Header';
import Topics from './Topics';
import FeatureIntroduction from './FeatureIntroduction';
import PreSubscriptionBanner from './PreSubscriptionBanner';

interface ISyllabusHomePageProps extends ISyllabusHomePageHeaderProps {
  topicUID: string;
  topicFilters: {
    educatorOptions: [];
    subjectOptions: [];
  };
  isRecordedGoal: boolean;
}

const HomePage: React.FC<ISyllabusHomePageProps> = ({
  source,
  goalUID,
  topicUID,
  isGTP,
  topicFilters,
  isRecordedGoal
}) => {
  const [selectedEducator, setSelectedEducator] = useState('all');
  const endpointParams = useMemo(() => {
    if (selectedEducator !== 'all' && isRecordedGoal) {
      return { educator_uid: selectedEducator };
    }

    return {};
  }, [selectedEducator, isRecordedGoal]);

  const { query } = useRouter();
  const { goalSlug } = query;
  const isSyllabusVisited = cookies.readCookie(
    'syllabusHomepageVisited',
    false
  );

  const isSubscribed = useMyInfo(getActiveUidsByType).includes(
    goalUID.toUpperCase()
  );

  const { syllabusData, isLoading } = useSyllabus(
    goalUID,
    true,
    endpointParams,
    isRecordedGoal,
    {
      fallbackData: undefined
    }
  );

  const goalSegmentData = useGoalEventProps({ goalUID });
  const topicCardClick = (topic) => {
    const { title, level } = topic;
    segment.track(
      SYLLABUS_CARD_CLICKED,
      {
        ...goalSegmentData,
        last_primary_source: SYLLABUS,
        last_primary_source_section: 'Syllabus',
        topology_name: title,
        topology_level: level
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      SYLLABUS_CLICKED_KEYS
    );
  };

  const handleGetSubscription = () => {
    segment.track(CHECKOUT_STARTED, {
      ...goalSegmentData,
      last_primary_source_section: 'Get subscription banner'
    });
    pushRoute(
      '/goal/[goalSlug]/[goalUID]/subscribe',
      `/goal/${goalSlug}/${goalUID}/subscribe`
    );
  };

  const handleEducatorSelection = (educatorUid) => {
    setSelectedEducator(educatorUid);
  };

  return (
    <>
      <Header source={source} goalUID={goalUID} isGTP={isGTP} />

      {!isSubscribed && (
        <PreSubscriptionBanner
          title="Learn with structured lessons"
          description="The subscription unlocks high quality lecture videos for the entire syllabus for all your subjects"
          handleButtonClick={handleGetSubscription}
          isGTP={isGTP}
        />
      )}
      <Topics
        goalUID={goalUID}
        goalSlug={goalSlug}
        data={syllabusData?.data}
        topicUID={topicUID}
        topicCardClick={topicCardClick}
        onEducatorSelection={handleEducatorSelection}
        topicFilters={topicFilters}
        selectedEducator={selectedEducator}
        isRecordedGoal={isRecordedGoal}
        isGTP={isGTP}
        isLoading={isLoading}
      />

      {!isSyllabusVisited && !isGTP && (
        <FeatureIntroduction
          goalUID={goalUID}
          isRecordedGoal={isRecordedGoal}
        />
      )}
    </>
  );
};

export default HomePage;
